import { t } from 'i18next';
import { showErrorSnackbar } from '../redux_store/slices/snackbarSlice';
import { ERROR } from '../constants/Colors';

export const ValidateInput = (regex: RegExp, input: string) => {
  return regex.test(input) ? true : false;
};

export const ValidateMobile = (inputValue: string) => {
  return ValidateInput(new RegExp('^[0-9]{10}$'), inputValue);
};

export const ValidateName = (inputValue: string) => {
  return ValidateInput(new RegExp('^[A-Za-z ]*$'), inputValue);
};

// export const ValidatePincode = (inputValue: string) => {
// 	return ValidateInput(new RegExp("^[0-9]{6}$"), inputValue);
// };

export const ValidateDob = (inputValue: string) => {
  return ValidateInput(
    new RegExp('^([1|2][0-9]{3})[-]([0]?[1-9]|[1][0-2])[-]([0]?[1-9]|[1|2][0-9]|[3][0|1])$'),
    inputValue
  );
};

export const ValidateExpiryDate = (inputValue: string) => {
  return ValidateInput(
    new RegExp('^([1|2][0-9]{3})[-]([0]?[1-9]|[1][0-2])[-]([0]?[1-9]|[1|2][0-9]|[3][0|1])$'),
    inputValue
  );
};

export const ValidateEmail = (inputValue: string) => {
  return ValidateInput(new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'), inputValue);
};

export const ValidateAddress = (inputValue: string) => {
  return ValidateInput(new RegExp('^[A-Za-z , 0-9]*$'), inputValue);
};

export const ValidateAccountNumber = (inputValue: string) => {
  return ValidateInput(new RegExp('^[A-Z0-9]{20}$'), inputValue);
};

export const ValidateUsername = (inputValue: string) => {
  return ValidateInput(new RegExp('^[A-Za-z0-9]*$'), inputValue);
};

// Common validation function
export function validateField(
  dispatch: any,
  fieldName: string,
  fieldValue: string,
  formRef?: any,
  validationPattern?: RegExp | undefined,
  requiredMessage?: string | undefined,
  invalidMessage?: string | undefined
): boolean {
  let isValid = true;
  if (!fieldValue) {
    dispatch(
      showErrorSnackbar({ message: `${t(fieldName)} ${t(requiredMessage ?? 'IS_REQUIRED')}` })
    );
    isValid = false;
  }

  if (validationPattern && !ValidateInput(validationPattern, fieldValue)) {
    dispatch(
      showErrorSnackbar({ message: `${t(invalidMessage ?? 'PLEASE_ENTER_VALID')} ${t(fieldName)}` })
    );
    isValid = false;
  }

  if (!isValid) {
    if (formRef?.current) {
      //let element: any = formRef?.current.querySelector('#name');
      let element: any = formRef?.current.querySelector('[id="' + t(fieldName) + '"]');

      if (element instanceof HTMLElement) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });

        const inputContainer: any = element.closest('.MuiOutlinedInput-root');
        if (inputContainer) {
          inputContainer.classList.add('Mui-focused');
          const notchedOutline: any = inputContainer.querySelector(
            '.MuiOutlinedInput-notchedOutline'
          );
          if (notchedOutline) {
            notchedOutline.style.borderWidth = '1.5px';
            notchedOutline.style.borderColor = ERROR;

            // Add an event listener to reset the border color to PRIMARY when input is valid
            element?.addEventListener('input', () => {
              if ((element as HTMLInputElement)?.value?.trim() !== '') {
                notchedOutline.style.borderColor = ''; // Reset to default (PRIMARY)
              }
            });

            element?.addEventListener('blur', () => {
              if ((element as HTMLSelectElement)?.value?.trim() !== '') {
                notchedOutline.style.borderColor = '';
                // notchedOutline.style.borderWidth = '1.5px'; // Reset to default (PRIMARY)
              }
            });
          }
        }
      }
    }
  }

  return isValid;
}

export function isParsableToFloat(str: string): boolean {
  // Check if the string is not empty and parse it to float
  const parsed = parseFloat(str);
  // Return true if parsed value is a valid number and the string is not equal to NaN or Infinity
  return !isNaN(parsed) && isFinite(parsed);
}

export function removePercentageFromTax(str: string): string {
  return (
    str
      ?.split(' - ', 10)
      ?.pop()
      ?.replace('%', '') || ''
  );
}

export function prepareMobileNumber(value: any) {
  // Remove any non-digit characters
  const input = value.replace(/\D/g, '').substring(0, 15);

  const first = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);
  const extra = input.substring(10, 15);

  if (input.length > 10) {
    return `(${first})-${middle}-${last}-${extra}`;
  } else if (input.length > 6) {
    return `(${first})-${middle}-${last}`;
  } else if (input.length > 3) {
    return `(${first})-${middle}`;
  } else if (input.length >= 0) {
    return input;
  }
}

// export function preparePostalCode(value: any) {
//   // Remove any non-alphanumeric characters (just in case)
//   const cleanPostcode = value.replace(/[^a-zA-Z0-9]/g, '');
//   // Ensure the postcode length is valid (usually 6 characters)
//   if (cleanPostcode.length > 3) {
//     // Split the postcode into two parts and format as XXX XXX
//     const formattedPostcode = `${cleanPostcode.slice(0, 3)} ${cleanPostcode.slice(3)}`;
//     return formattedPostcode?.toUpperCase();
//   } else {
//     return cleanPostcode;
//   }
// }

export function preparePostalCode(value: any, country: any) {
  const cleanPostcode = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  if (country === 'USA') {
    const numericPostcode = cleanPostcode.replace(/\D/g, '');

    if (numericPostcode.length >= 6) {
      // const formattedPostcode = `${numericPostcode.slice(0, 5)}-${numericPostcode.slice(5, 9)}`;
      const formattedPostcode =
        numericPostcode.length > 6
          ? `${numericPostcode.slice(0, 5)}-${numericPostcode.slice(5, 9)}`
          : numericPostcode;
      return formattedPostcode;
    } else {
      return numericPostcode;
    }
  } else {
    if (cleanPostcode.length > 3) {
      const formattedPostcode = `${cleanPostcode.slice(0, 3)} ${cleanPostcode.slice(3)}`;
      return formattedPostcode;
    } else {
      return cleanPostcode;
    }
  }
}

export function prepareDemicalNumber(value: any) {
  if (value.startsWith('.')) {
    value = '0' + value;
  }
  return value;
}
